import React, {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {selectSelectedToken, selectTutorialState} from '../../../redux/reducers/privateStateReducer/selectors';
import {
  selectPendingShootingActions,
  selectUnits,
  selectWarPhaseStage
} from '../../../redux/reducers/sharedStateReducer/selectors';
import {getTutorialChapter} from '../../../configs/tutorial';
import {ANY_COMMON_BUILDING_TOKEN, ANY_SPECIAL_BUILDING_TOKEN} from '../../../constants/tokens';
import './TutorialUiArrow.css';

/**
 * Стрелка-указатель на элемент интерфейса (arrow_down_big.png).
 * Позиционируется над элементом по getBoundingClientRect; позиция
 * пересчитывается интервалом — элементы футера/шапки могут двигаться
 * (сворачивание футера, ресайз).
 *
 * Целится по списку селекторов-кандидатов (первый найденный в DOM):
 * - шаг с постройкой (allow build/buildSpecial), пока нужный тип строения НЕ
 *   выбран в тулбаре, — на кнопку этого типа в NewBuildingToolbar
 *   (data-tutorial-select), а если открыта не та панель тулбара — на кнопку
 *   переключения панели (обычные/специальные строения);
 * - шаг найма нового отряда (allow recruitUnit), пока нужный тип отряда не
 *   выбран, — на кнопку типа в WarRecruitmentStageToolbar;
 * - иначе — task.arrowUiSelector из конфига шага; у двухэтапных шагов
 *   (task.arrowHexKeyStage) DOM-стрелка гаснет, когда наступил этап войны
 *   для стрелки на ячейку (ее рисует HexGridRenderer).
 *
 * Пока открыта обучающая модалка, стрелка скрыта — кроме шагов с
 * step.modalArrowUiSelector: такая стрелка показывается ПОВЕРХ модалки, пока
 * та открыта (модалка рассказывает про кнопку — стрелка ее показывает).
 */
const POSITION_UPDATE_INTERVAL_MS = 300;

// Если над целью не хватает места под стрелку (цель у верхнего края экрана,
// например кнопка панели разделов правил) — стрелка переворачивается и
// показывает на цель снизу вверх
const ARROW_FLIP_MIN_TOP_SPACE_PX = 110;

// Селекторы-кандидаты текущего шага в порядке приоритета
export const getSelectorCandidates = (activeStep, selectedToken, warPhaseStage, isStepShotTaken) => {
  if (!activeStep) {
    return [];
  }

  const allowDeclarations = activeStep.allow || [];

  // Покупка политики — цепочка: кнопка «Купить» на открытой карточке ->
  // карточка нужной политики в открытой панели рынка -> кнопка открытия рынка
  // (task.arrowUiSelectorCandidates или task.arrowUiSelector: в шапке на
  // десктопе, ярлык на телефоне). Проверяется раньше явной цепочки шага:
  // иначе, пока рынок открыт, стрелка оставалась бы на кнопке его открытия.
  // Невидимые элементы (закрытый рынок) отсеивает проверка видимости при
  // позиционировании
  const buyPolicyDeclaration = allowDeclarations.find((declaration) =>
    declaration.action === 'buyPolicy' && declaration.policyId);
  if (buyPolicyDeclaration) {
    return [
      '[data-tutorial-select="policy_buy_button"]',
      `[data-tutorial-select="${buyPolicyDeclaration.policyId}"]`,
      ...(activeStep.task?.arrowUiSelectorCandidates
        || (activeStep.task?.arrowUiSelector ? [activeStep.task.arrowUiSelector] : [])),
    ];
  }

  // Явная цепочка селекторов из конфига шага (первый ВИДИМЫЙ побеждает):
  // например, пункт «Правила» в меню матча -> кнопка меню в шапке
  if (activeStep.task?.arrowUiSelectorCandidates) {
    return activeStep.task.arrowUiSelectorCandidates;
  }

  const buildDeclaration = allowDeclarations.find((declaration) =>
    (declaration.action === 'build' || declaration.action === 'buildSpecial') && declaration.token);
  if (buildDeclaration && selectedToken !== buildDeclaration.token) {
    // Нужная кнопка может быть на другой панели тулбара (обычные строения <->
    // специальные) — тогда стрелка указывает на кнопку переключения панелей
    const panelToggleId = buildDeclaration.action === 'buildSpecial'
      ? ANY_SPECIAL_BUILDING_TOKEN.id
      : ANY_COMMON_BUILDING_TOKEN.id;
    return [
      `[data-tutorial-select="${buildDeclaration.token}"]`,
      `[data-tutorial-select="${panelToggleId}"]`,
    ];
  }

  const recruitDeclaration = allowDeclarations.find((declaration) =>
    declaration.action === 'recruitUnit' && declaration.unitType);
  if (recruitDeclaration && selectedToken !== recruitDeclaration.unitType) {
    return [`[data-tutorial-select="${recruitDeclaration.unitType}"]`];
  }

  if (activeStep.task?.arrowHexKeyStage && warPhaseStage === activeStep.task.arrowHexKeyStage) {
    // На «своем» этапе войны DOM-стрелка гаснет (цель на поле показывает
    // HexGridRenderer). Исключение — выстрел шага уже сделан: целей на поле
    // больше нет, стрелка возвращается на кнопку завершения этапа
    return isStepShotTaken && activeStep.task?.arrowUiSelector
      ? [activeStep.task.arrowUiSelector]
      : [];
  }

  return activeStep.task?.arrowUiSelector ? [activeStep.task.arrowUiSelector] : [];
};

// Кандидат годится, только если элемент реально видим (панели-ящики вроде
// рынка политик всегда в DOM и лишь задвинуты за край экрана)
const checkIfElementVisible = (element) => {
  // display:none/visibility:hidden/content-visibility. Без опций
  // checkVisibility проверяет только display/content-visibility — свойство
  // visibility включается опцией (visibilityProperty — актуальное имя,
  // checkVisibilityCSS — прежнее; лишний ключ браузер игнорирует). В очень
  // старых браузерах метода нет — тогда полагаемся на проверку rect
  if (typeof element.checkVisibility === 'function'
    && !element.checkVisibility({visibilityProperty: true, checkVisibilityCSS: true})) {
    return false;
  }
  const rect = element.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0
    && rect.right > 0 && rect.bottom > 0
    && rect.left < window.innerWidth && rect.top < window.innerHeight;
};

// Кандидат не годится и когда его центр НАКРЫТ чужим элементом (например,
// плашка в панели игроков под поднятой поверх панелью рынка политик):
// elementFromPoint возвращает верхний отрисованный элемент — если он не
// связан с кандидатом отношением предок/потомок, кандидат перекрыт и
// очередь переходит к следующему селектору. Сама стрелка с
// pointer-events: none хит-тесту не мешает
const checkIfElementUnoccluded = (element) => {
  const rect = element.getBoundingClientRect();
  const topElement = document.elementFromPoint(
    Math.min(Math.max(rect.left + rect.width / 2, 0), window.innerWidth - 1),
    Math.min(Math.max(rect.top + rect.height / 2, 0), window.innerHeight - 1),
  );
  if (!topElement) {
    return false;
  }
  return topElement === element || element.contains(topElement) || topElement.contains(element);
};

// Цель шага перекрыта открытым оверлеем — выпадающим списком шапки (на
// телефоне рынок — лист снизу) или модалкой правил, когда сам шаг не про них:
// стрелка ведет игрока к кнопке закрытия оверлея. Список, не накрывающий
// цель, геометрия (центр цели внутри rect списка) отфильтрует сама
const resolveOverlayCloseTarget = (targetElement) => {
  const rulesOverlay = document.querySelector('.rules-modal_overlay');
  if (rulesOverlay) {
    // Цель шага ВНУТРИ открытых правил (бургер панели разделов, ссылка
    // раздела): правила рисуются поверх всех боковых панелей, так что ни
    // одна «открытая» под ними панель цель не перекрывает — редирект на
    // кнопки закрытия не нужен вовсе. Без этого выхода на мобильном стрелка
    // вела на крестик панели игроков, оставшейся «открытой» ПОД правилами
    if (rulesOverlay.contains(targetElement)) {
      return null;
    }
    const rulesCloseButton = rulesOverlay.querySelector('.rules-modal_close-button');
    if (rulesCloseButton && checkIfElementVisible(rulesCloseButton)) {
      return rulesCloseButton;
    }
  }
  const targetRect = targetElement.getBoundingClientRect();
  const targetCenterX = targetRect.left + targetRect.width / 2;
  const targetCenterY = targetRect.top + targetRect.height / 2;
  // Открытые выпадающие списки шапки (на телефоне рынок — лист снизу)
  for (const sideMenu of document.querySelectorAll('.header-dropdown_open')) {
    if (sideMenu.contains(targetElement)) {
      continue;
    }
    const menuRect = sideMenu.getBoundingClientRect();
    const isTargetCovered = targetCenterX >= menuRect.left && targetCenterX <= menuRect.right
      && targetCenterY >= menuRect.top && targetCenterY <= menuRect.bottom;
    if (!isTargetCovered) {
      continue;
    }
    const closeButton = sideMenu.querySelector('.header-dropdown_close-button');
    if (closeButton && checkIfElementVisible(closeButton)) {
      return closeButton;
    }
  }
  return null;
};

// Ключ-маркер: у шага только ячеечная цель (DOM-селекторов нет), но интервал
// позиционирования нужен — открытый оверлей перекрывает и карту
const HEX_TARGET_ONLY_KEY = '__hex_target_only__';

export const TUTORIAL_ARROW_IMAGE = '/img/icons/arrow_down_big.png';
export const TUTORIAL_ARROW_WAIT_IMAGE = '/img/icons/arrow_down_big_wait.png';

// Цель сама в ожидании (кнопка завершения хода, пока ходит противник, —
// data-tutorial-waiting): нажимать нечего, стрелка «ждать» с песочными часами.
// Смотрим только на саму цель: иконки фракции и политики внутри такой кнопки —
// отдельные цели, их как раз нажимают, и стрелка на них обычная
const checkIfArrowTargetWaiting = (element) => element.getAttribute('data-tutorial-waiting') === 'true';

const TutorialUiArrow = () => {
  const tutorialState = useSelector(selectTutorialState);
  const selectedToken = useSelector(selectSelectedToken);
  const warPhaseStage = useSelector(selectWarPhaseStage);
  const units = useSelector(selectUnits);
  const pendingShootingActions = useSelector(selectPendingShootingActions);
  const [position, setPosition] = useState(null);

  // Стрелка «ждать» подменяет обычную при смене хода — картинка должна быть
  // загружена заранее, иначе на месте стрелки мигает пустота
  useEffect(() => {
    if (typeof Image !== 'undefined') {
      new Image().src = TUTORIAL_ARROW_WAIT_IMAGE;
    }
  }, []);

  const chapter = getTutorialChapter(tutorialState.chapterId);
  const activeStep = chapter ? chapter.steps[tutorialState.stepIndex] : null;
  // Выстрел текущего шага уже сделан (pending): стреляла катапульта шага
  // (allow shoot.fromHexKey) — см. getSelectorCandidates
  const stepShootDeclaration = (activeStep?.allow || []).find(
    (declaration) => declaration.action === 'shoot' && declaration.fromHexKey);
  const stepShooterUnit = stepShootDeclaration
    ? (units || []).find((unit) => unit.hexKey === stepShootDeclaration.fromHexKey)
    : null;
  const isStepShotTaken = Boolean(stepShooterUnit
    && (pendingShootingActions || []).some((action) => action.shooterUnitId === stepShooterUnit.id));
  const isModalOpen = Boolean(tutorialState.activeModalId);
  const modalArrowUiSelector = isModalOpen ? activeStep?.modalArrowUiSelector : null;
  // Модалка шага еще не показана (ждет modalDelayMs или закрытия игровой
  // модалки) — стрелки не показываем: игрок еще не прочитал новое задание
  const isStepModalPending = Boolean(activeStep?.modalId && !tutorialState.isStepModalShown);
  let selectorCandidates = [];
  if (!tutorialState.isChapterCompleted) {
    if (isModalOpen) {
      selectorCandidates = modalArrowUiSelector ? [modalArrowUiSelector] : [];
    } else if (!isStepModalPending) {
      selectorCandidates = getSelectorCandidates(activeStep, selectedToken, warPhaseStage, isStepShotTaken);
    }
  }
  const hasHexArrowTarget = Boolean(activeStep?.task?.arrowHexKey
    || activeStep?.task?.inspectUnitsHexKeys?.length);
  const selectorsKey = selectorCandidates.join('|')
    || (!tutorialState.isChapterCompleted && !isModalOpen && !isStepModalPending && hasHexArrowTarget
      ? HEX_TARGET_ONLY_KEY
      : '');
  // Поверх модалок: стрелка при открытой обучающей модалке
  // (modalArrowUiSelector) или шаг с целями внутри игровой модалки
  // (task.arrowAboveModals — например, разделы открытых правил)
  const isAboveModal = Boolean(modalArrowUiSelector) || Boolean(activeStep?.task?.arrowAboveModals);

  // Стрелка при открытой обучающей модалке (modalArrowUiSelector) нарочно
  // целится под модалку — проверка перекрытия для нее отключается
  const isModalArrowMode = Boolean(modalArrowUiSelector);
  // Стрелка над каждым совпадением победившего селектора, а не только первым
  const isMultiple = Boolean(activeStep?.task?.arrowUiSelectorMultiple);

  useEffect(() => {
    if (!selectorsKey) {
      setPosition(null);
      return;
    }
    const selectors = selectorsKey === HEX_TARGET_ONLY_KEY ? [] : selectorsKey.split('|');

    const updatePosition = () => {
      let elements = [];
      for (const selector of selectors) {
        // Помеченный элемент может быть смонтирован в нескольких местах
        // (например, карточка политики: список игроков, панель рынка,
        // правила) — берем реально ВИДИМЫЕ и НЕ ПЕРЕКРЫТЫЕ совпадения, а не
        // первое в DOM: невидимый или накрытый другой панелью дубль иначе
        // гасит весь селектор-кандидат. Обычно стрелка одна (первое
        // совпадение); шаг с task.arrowUiSelectorMultiple получает стрелку
        // над КАЖДЫМ совпадением победившего селектора (например, каждая
        // еще не выбранная кость переброса)
        const matches = [...document.querySelectorAll(selector)]
          .filter((candidate) => checkIfElementVisible(candidate)
            && (isModalArrowMode || checkIfElementUnoccluded(candidate)));
        if (matches.length > 0) {
          elements = isMultiple ? matches : [matches[0]];
          break;
        }
      }
      // Перекрытие открытым оверлеем: для DOM-цели — ее самой, для
      // ячеечной стрелки (селекторов нет) — карты. Стрелка переезжает на
      // кнопку закрытия оверлея и рисуется поверх него
      const referenceElement = elements[0] || document.querySelector('canvas');
      const overlayCloseButton = referenceElement ? resolveOverlayCloseTarget(referenceElement) : null;
      let isOverlayClose = false;
      if (overlayCloseButton) {
        elements = [overlayCloseButton];
        isOverlayClose = true;
      }
      if (elements.length === 0) {
        setPosition((prev) => (prev === null ? prev : null));
        return;
      }
      const nextPositions = elements.map((element) => {
        // Контейнеры кнопок тулбаров шире видимой кнопки (рядом цена/
        // скидка) — целимся во вложенную иконку отряда или кнопку строения
        const innerAnchorElement = element.querySelector('.unit-icon')
          || element.querySelector('.toolbar-button');
        const anchorElement = innerAnchorElement || element;
        const rect = anchorElement.getBoundingClientRect();
        const isFlipped = rect.top < ARROW_FLIP_MIN_TOP_SPACE_PX;
        return {
          left: Math.round(rect.left + rect.width / 2),
          top: Math.round(isFlipped ? rect.bottom : rect.top),
          isFlipped,
          // Кнопка закрытия оверлея — всегда «нажать»
          isWaiting: !isOverlayClose && checkIfArrowTargetWaiting(element),
        };
      });
      const next = {positions: nextPositions, isOverlayClose};
      setPosition((prev) => {
        const isSame = prev && prev.isOverlayClose === next.isOverlayClose
          && prev.positions.length === next.positions.length
          && prev.positions.every((prevPosition, index) =>
            prevPosition.left === next.positions[index].left
            && prevPosition.top === next.positions[index].top
            && prevPosition.isFlipped === next.positions[index].isFlipped
            && prevPosition.isWaiting === next.positions[index].isWaiting);
        return isSame ? prev : next;
      });
    };

    updatePosition();
    const intervalId = setInterval(updatePosition, POSITION_UPDATE_INTERVAL_MS);
    window.addEventListener('resize', updatePosition);
    return () => {
      clearInterval(intervalId);
      window.removeEventListener('resize', updatePosition);
    };
  }, [selectorsKey, isModalArrowMode, isMultiple]);

  if (!position || position.positions.length === 0) {
    return null;
  }

  return (
    <>
      {position.positions.map((arrowPosition, index) => (
        <img
          key={index}
          className={`tutorial-ui-arrow ${isAboveModal || position.isOverlayClose ? 'tutorial-ui-arrow_above-modal' : ''} ${arrowPosition.isFlipped ? 'tutorial-ui-arrow_flipped' : ''}`}
          style={{left: arrowPosition.left, top: arrowPosition.top}}
          src={arrowPosition.isWaiting ? TUTORIAL_ARROW_WAIT_IMAGE : TUTORIAL_ARROW_IMAGE}
          alt={''}
          aria-hidden={'true'}
        />
      ))}
    </>
  );
};

export default TutorialUiArrow;
